import React, { useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  KeyboardCode,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  LayoutConfig,
  LayoutElementType,
} from "@/components/layout/element-layout/layoutTypes";
import ElementLayout from "@/components/layout/element-layout/ElementLayout";
import RowEditor from "./RowEditor";
import RowSettingsSidebar from "./RowSettingsSidebar";
import IconProvider from "@/components/ui/IconProvider";
import AddRowContextMenu from "./AddRowContextMenu";
import { useRightSidebarConfig } from "@/components/layout/side-bars/right-context-sidebar";
import { useSidebar } from "@/components/layout/side-bars/sidebar";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export interface PageRow {
  id: string;
  title?: string;
  layoutConfig: LayoutConfig;
  backgroundColor?: string; // NEW
}

export interface PageLayoutConfig {
  rows: PageRow[];
  title?: string;
  spacing?: number; // spacing between rows in pixels
  padding?: number; // padding around the entire page layout
}

export interface PageLayoutProps {
  config: PageLayoutConfig;
  className?: string;
  style?: React.CSSProperties;
  onConfigChange?: (newConfig: PageLayoutConfig) => void;
  editMode?: boolean;
  onEditModeChange?: (editMode: boolean) => void;
  showTitle?: boolean;
}

export default function PageLayout({
  config,
  className = "",
  style = {},
  onConfigChange,
  editMode: externalEditMode,
  onEditModeChange,
  showTitle = true,
}: PageLayoutProps) {
  const { t } = usePlatformTranslation();
  const [internalEditMode, setInternalEditMode] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const addRowButtonRef = useRef<HTMLDivElement>(null);
  
  // Move all hooks to the top, before any conditional returns
  const { openCustom } = useRightSidebarConfig();
  const { state: sidebarState } = useSidebar();
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: {
        start: [KeyboardCode.Enter],
        cancel: ['Escape'],
        end: [KeyboardCode.Enter, KeyboardCode.Space],
      },
    })
  );

  // Add null check AFTER all hooks
  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('platform.layout.page-layout.no_configuration_available')}</p>
      </div>
    );
  }
  
  const { rows, title, spacing = 32, padding = 0 } = config;
  const editMode = externalEditMode !== undefined ? externalEditMode : internalEditMode;
  const setEditMode = onEditModeChange || setInternalEditMode;

  const handleAddRowClick = () => {
    setContextMenuOpen(true);
  };

  const handleAddRow = (widgetType: LayoutElementType = "stats-widget") => {
    if (!onConfigChange) return;

    // Check if this is a row layout type
    if (widgetType.startsWith("row-layout-")) {
      const slotCount = parseInt(widgetType.split("-")[2]); // Extract number from 'row-layout-X'

      const newRow: PageRow = {
        id: `row-${Date.now()}`,
        title: `Row ${rows.length + 1}`,
        layoutConfig: {
          elements: [], // Start with no elements
          totalSlots: slotCount, // Set the number of slots
          gap: 40,
          padding: 0,
        },
      };

      const newConfig = {
        ...config,
        rows: [...rows, newRow],
      };

      onConfigChange(newConfig);
    } else {
      // Handle regular widget types (existing behavior)
      const newRow: PageRow = {
        id: `row-${Date.now()}`,
        title: `Row ${rows.length + 1}`,
        layoutConfig: {
          elements: [
            {
              id: `element-${Date.now()}`,
              type: widgetType,
              props: {},
            },
          ],
          gap: 40,
          padding: 0,
        },
      };

      const newConfig = {
        ...config,
        rows: [...rows, newRow],
      };

      onConfigChange(newConfig);
    }
  };

  const handleRemoveRow = (rowId: string) => {
    if (!onConfigChange || rows.length <= 1) return;

    const newConfig = {
      ...config,
      rows: rows.filter((row) => row.id !== rowId),
    };

    onConfigChange(newConfig);
  };

  const handleRowUpdate = (rowId: string, updates: Partial<PageRow>) => {
    if (!onConfigChange) return;

    const newConfig = {
      ...config,
      rows: rows.map((row) =>
        row.id === rowId ? { ...row, ...updates } : row
      ),
    };

    onConfigChange(newConfig);
  };

  const handleRowConfigChange = (
    rowId: string,
    newLayoutConfig: LayoutConfig
  ) => {
    if (!onConfigChange) return;

    const newConfig = {
      ...config,
      rows: rows.map((row) =>
        row.id === rowId ? { ...row, layoutConfig: newLayoutConfig } : row
      ),
    };

    onConfigChange(newConfig);
  };

  const handleRowSettingsClick = (row: PageRow, rowIndex: number) => {
    openCustom({
      title: `Row ${rowIndex + 1} Settings`,
      description: `Configure settings for ${
        row.title || `Row ${rowIndex + 1}`
      }`,
      content: (
        <RowSettingsSidebar
          row={row}
          rowIndex={rowIndex}
          onRowUpdate={handleRowUpdate}
          onRemove={() => handleRemoveRow(row.id)}
          canRemove={rows.length > 1}
        />
      ),
      showSaveButton: false, // We handle save/reset in the sidebar component
      showResetButton: false,
      showCloseButton: true,
    });
  };


  // Handle drag end for row reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && onConfigChange) {
      const oldIndex = rows.findIndex((row) => row.id === active.id);
      const newIndex = rows.findIndex((row) => row.id === over?.id);

      const newRows = arrayMove(rows, oldIndex, newIndex);
      const newConfig = {
        ...config,
        rows: newRows,
      };

      onConfigChange(newConfig);
    }
  };

  // Sortable Row Component
  const SortableRow = ({ row, index }: { row: PageRow; index: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: row.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      // Fix for drag preview deformation
      width: isDragging ? "100%" : undefined,
      height: isDragging ? "auto" : undefined,
      position: isDragging ? ("relative" as const) : undefined,
      zIndex: isDragging ? 1000 : undefined,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`relative ${isDragging ? "drag-preview" : ""} h-full min-h-0 flex flex-col`}
      >
        {editMode && (
          <RowEditor
            row={row}
            rowIndex={index}
            onRemove={() => handleRemoveRow(row.id)}
            canRemove={rows.length > 1}
            onSettingsClick={() => handleRowSettingsClick(row, index)}
            dragHandleProps={{ attributes, listeners }}
          />
        )}

        <div
          className="rounded-lg transition-colors pt-4 h-full min-h-0 flex-1 flex flex-col"
          style={{
            background: row.backgroundColor || "transparent",
            boxShadow: "none",
            // Ensure proper sizing during drag
            minHeight: isDragging ? "auto" : undefined,
            maxWidth: isDragging ? "100%" : undefined,
          }}
        >
          <ElementLayout
            config={row.layoutConfig}
            editMode={editMode}
            onConfigChange={(newLayoutConfig) =>
              handleRowConfigChange(row.id, newLayoutConfig)
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-full h-full min-h-0 overflow-y-auto overflow-x-hidden">
      <div
        className={className + " relative bg-transparent flex flex-col w-full max-w-full min-h-full"}
        style={{
          ...style,
          paddingTop: padding > 0 ? `${padding}px` : 0,
          paddingLeft: padding > 0 ? `${padding}px` : 0,
          paddingBottom: 0,
        }}
      >
        {/* Rows */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[
            // Ensure drag preview maintains proper dimensions and prevents deformation
            (args) => {
              const { transform } = args;
              return {
                ...transform,
                scaleX: 1,
                scaleY: 1,
                rotateX: 0,
                rotateY: 0,
                rotateZ: 0,
              };
            },
          ]}
        >
          <SortableContext
            items={rows.map((row) => row.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{ gap: `${spacing}px`,
              paddingBottom: padding > 0 ? `${padding}px` : 0,
              paddingRight: padding > 0 ? `${padding}px` : 0,
            }}
              className="flex flex-col min-h-0 flex-1 h-full"
              
            >
              {rows.map((row, index) => (
                <SortableRow key={row.id} row={row} index={index} />
              ))}
              
              {/* Add Row Button - positioned under the last element */}
              {editMode && (
                <div className="flex justify-center mt-4">
                  <div
                    ref={addRowButtonRef}
                    className="cursor-pointer hover:text-primary transition-colors"
                    onClick={handleAddRowClick}
                  >
                    <div className="bg-white border-2 border-dashed border-input rounded-lg p-4 hover:border-primary hover:bg-muted transition-colors flex items-center gap-2 shadow-sm">
                      <IconProvider
                        iconstring="fa-circle-plus"
                        size={20}
                        color="#6B7280"
                        className="text-primary"
                      />
                      <span className="text-sm font-medium text-foreground">{t('platform.layout.page-layout.add_new_row')}</span>
                    </div>
                  </div>

                  <AddRowContextMenu
                    isOpen={contextMenuOpen}
                    onClose={() => setContextMenuOpen(false)}
                    onSelectWidget={handleAddRow}
                    triggerRef={addRowButtonRef}
                  />
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
